import pdfMake from "pdfmake/build/pdfmake"
import pdfFonts from "pdfmake/build/vfs_fonts"
import { marked, type Token } from "marked"
import { supabase } from "../../db"

(pdfMake as any).vfs = (pdfFonts as any)?.pdfMake?.vfs ?? pdfFonts

const HEADINGS = [20, 16, 14, 12, 11, 10]
const WIDTH = 515
const GAP = [0, 0, 0, 6]

// The model writes the document and the contact gets the file. What comes back to the model is only the
// confirmation, so it does not paste the content into the reply as well
export function pdfTool(config: Extract<PlaybookToolConfig, { type: "pdf" }>, index: number, files: string[]): PlaybookTool {
  return {
    name: `pdf_${index}`,
    description: `${config.description}. The file is sent to the contact with your message: do not repeat its content`,
    parameters: {
      type: "object",
      properties: {
        title: { type: "string" },
        markdown: { type: "string", description: "The whole document: headings, paragraphs, lists and tables" },
      },
      required: ["title", "markdown"],
    },
    run: async ({ title, markdown }) => {
      const name = `playbooks/${Date.now()}-${slug(title)}.pdf`
      const body = new Blob([await render(title, markdown)], { type: "application/pdf" })
      const { data, error } = await supabase.storage.from("temp").upload(name, body, { cacheControl: "300" })
      if (error) return `The PDF could not be sent: ${error.message}`
      files.push(supabase.storage.from("temp").getPublicUrl(data.path).data.publicUrl)
      return "The PDF was sent."
    },
  }
}

async function render(title: string, markdown: string): Promise<Uint8Array> {
  const content = [{ text: title, fontSize: HEADINGS[0], bold: true, margin: [0, 0, 0, 10] }, ...blocks(marked.lexer(clean(markdown)))]
  const doc = { content, defaultStyle: { font: "Roboto", fontSize: 10, lineHeight: 1.3 }, pageMargins: [40, 40, 40, 40] }
  return new Uint8Array(await pdfMake.createPdf(doc as any).getBuffer())
}

// Roboto has no emoji and pdfmake renders html as text, so both go
function clean(markdown: string): string {
  return markdown
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, "\"").replace(/&#39;/g, "'").replace(/&amp;/g, "&")
    .replace(/[\p{Extended_Pictographic}️‍]/gu, "")
}

function inline(tokens: Token[] = []): any[] {
  return tokens.flatMap((t: any): any[] => {
    switch (t.type) {
      case "strong": return inline(t.tokens).map(s => ({ ...s, bold: true }))
      case "em": return inline(t.tokens).map(s => ({ ...s, italics: true }))
      case "codespan": return [{ text: t.text, background: "#f0f0f0" }]
      case "link": return inline(t.tokens).map(s => ({ ...s, link: t.href, color: "#2980b9", decoration: "underline" }))
      case "br": return [{ text: "\n" }]
      default: return t.tokens?.length ? inline(t.tokens) : [{ text: t.text ?? "" }]
    }
  })
}

function line(width: number) {
  return { canvas: [{ type: "line", x1: 0, y1: 0, x2: WIDTH, y2: 0, lineWidth: width, lineColor: "#bdc3c7" }], margin: [0, 0, 0, 6] }
}

function blocks(tokens: Token[]): any[] {
  return tokens.flatMap((t: any): any[] => {
    switch (t.type) {
      case "heading": return [
        { text: inline(t.tokens), fontSize: HEADINGS[t.depth - 1] ?? 10, bold: true, margin: [0, t.depth <= 2 ? 12 : 8, 0, 4] },
        ...(t.depth <= 2 ? [line(t.depth === 1 ? 1 : 0.5)] : []),
      ]
      case "paragraph": return [{ text: inline(t.tokens), margin: GAP, lineHeight: 1.4 }]
      case "list": {
        const items = t.items.map((item: any) => ({ text: inline(item.tokens) }))
        return [t.ordered ? { ol: items, start: typeof t.start === "number" ? t.start : 1, margin: GAP } : { ul: items, margin: GAP }]
      }
      case "blockquote": return [{ stack: blocks(t.tokens), italics: true, color: "#555555", margin: [10, 0, 0, 6] }]
      case "code": return [{ text: t.text, fontSize: 9, preserveLeadingSpaces: true, background: "#f8f9fa", margin: GAP }]
      case "table": return [{
        table: {
          headerRows: 1,
          widths: Array(t.header.length).fill("*"),
          body: [t.header.map((h: any) => ({ text: inline(h.tokens), bold: true, fillColor: "#f0f0f0" })), ...t.rows.map((row: any[]) => row.map(c => ({ text: inline(c.tokens) })))],
        },
        layout: "lightHorizontalLines",
        margin: GAP,
      }]
      case "hr": return [line(0.5)]
      case "space":
      case "html": return []
      default: return t.text ? [{ text: t.text, margin: GAP }] : []
    }
  })
}

function slug(text: string): string {
  return (text || "document").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "document"
}
